import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { MessageCircle, Send, User, Search, ArrowLeft, Wifi, WifiOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API_URL.replace('https://', 'wss://').replace('http://', 'ws://') + '/api/ws';

export default function Chat() {
  const [conversations, setConversations] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [showUserList, setShowUserList] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [isTyping, setIsTyping] = useState(null);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const currentUserId = localStorage.getItem("user_id");
  const token = localStorage.getItem("token");

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (!token) return;
    
    const ws = new WebSocket(`${WS_URL}/chat/${token}`);
    
    ws.onopen = () => {
      console.log("WebSocket connected");
      setIsConnected(true);
    };
    
    ws.onclose = () => {
      console.log("WebSocket disconnected");
      setIsConnected(false);
      // Reconnect after 3 seconds
      setTimeout(connectWebSocket, 3000);
    };
    
    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
      setIsConnected(false);
    };
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === "new_message") {
        const msg = data.message;
        // Add message to current chat if it's from selected partner
        if (selectedPartner && msg.from_user_id === selectedPartner.id) {
          setMessages(prev => [...prev, msg]);
          setTimeout(scrollToBottom, 100);
          // Mark as read
          ws.send(JSON.stringify({ type: "read", partner_id: selectedPartner.id }));
        }
        // Update conversations
        fetchConversations();
        setIsTyping(null);
      }
      
      if (data.type === "message_sent") {
        const msg = data.message;
        setMessages(prev => [...prev, msg]);
        setTimeout(scrollToBottom, 100);
        fetchConversations();
      }
      
      if (data.type === "typing" && selectedPartner && data.from_user_id === selectedPartner.id) {
        setIsTyping(data.from_username);
        // Clear typing indicator after 3 seconds
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(() => setIsTyping(null), 3000);
      }
      
      if (data.type === "messages_read") {
        // Update read status for messages
        setMessages(prev => prev.map(m => 
          m.to_user_id === data.by_user_id ? { ...m, is_read: true } : m
        ));
      }
      
      if (data.type === "error") {
        toast.error(data.message);
      }
    };
    
    wsRef.current = ws;
  }, [token, selectedPartner]);

  useEffect(() => {
    connectWebSocket();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connectWebSocket]);

  const fetchConversations = async () => {
    try {
      const res = await fetch(`${API_URL}/api/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConversations(data);
      }
    } catch (err) {
      console.error("Error fetching conversations:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/users/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const fetchMessages = async (partnerId) => {
    try {
      const res = await fetch(`${API_URL}/api/messages/${partnerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(data);
        setTimeout(scrollToBottom, 100);
        // Mark as read via WebSocket
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: "read", partner_id: partnerId }));
        }
      }
    } catch (err) {
      console.error("Error fetching messages:", err);
    }
  };

  useEffect(() => {
    fetchConversations();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (selectedPartner) {
      fetchMessages(selectedPartner.id);
    }
  }, [selectedPartner]);

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedPartner) return;
    
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: "message",
        to_user_id: selectedPartner.id,
        content: newMessage.trim()
      }));
      setNewMessage("");
    } else {
      // Fallback to HTTP
      sendMessageHTTP();
    }
  };

  const sendMessageHTTP = async () => {
    try {
      const res = await fetch(`${API_URL}/api/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          to_user_id: selectedPartner.id,
          content: newMessage.trim()
        })
      });

      if (res.ok) {
        const msg = await res.json();
        setMessages(prev => [...prev, msg]);
        setNewMessage("");
        setTimeout(scrollToBottom, 100);
        fetchConversations();
      } else {
        toast.error("Ошибка отправки");
      }
    } catch (err) {
      toast.error("Ошибка сети");
    }
  };

  const handleTyping = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && selectedPartner) {
      wsRef.current.send(JSON.stringify({
        type: "typing",
        to_user_id: selectedPartner.id
      }));
    }
  };

  const startNewChat = (user) => {
    setSelectedPartner({ id: user.id, username: user.username });
    setShowUserList(false);
    setMessages([]);
  };

  const selectConversation = (conv) => {
    setSelectedPartner({ id: conv.partner_id, username: conv.partner_username });
  };

  const filteredUsers = users.filter(u =>
    u.username.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-120px)] flex" data-testid="chat-page">
      {/* Sidebar - Conversations */}
      <div className={`w-80 border-r border-slate-200 bg-white flex flex-col ${selectedPartner ? 'hidden md:flex' : 'flex'}`}>
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-slate-800">Сообщения</h2>
              {isConnected ? (
                <Wifi className="w-4 h-4 text-green-500" title="Подключено" />
              ) : (
                <WifiOff className="w-4 h-4 text-red-500" title="Отключено" />
              )}
            </div>
            <Button size="sm" variant="outline" onClick={() => setShowUserList(true)} data-testid="new-chat-btn">
              <MessageCircle className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="p-4 text-center">
              <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Нет диалогов</p>
              <Button 
                size="sm" 
                variant="link" 
                onClick={() => setShowUserList(true)}
                className="mt-2"
              >
                Начать новый чат
              </Button>
            </div>
          ) : (
            conversations.map(conv => (
              <button
                key={conv.partner_id}
                onClick={() => selectConversation(conv)}
                className={`w-full p-4 text-left hover:bg-slate-50 border-b border-slate-100 transition-colors ${
                  selectedPartner?.id === conv.partner_id ? 'bg-blue-50' : ''
                }`}
                data-testid={`conversation-${conv.partner_id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-slate-600 font-medium">
                      {conv.partner_username.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-800 truncate">{conv.partner_username}</span>
                      {conv.unread_count > 0 && (
                        <span className="bg-blue-500 text-white text-xs px-2 py-0.5 rounded-full">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 truncate">{conv.last_message}</p>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className={`flex-1 flex flex-col bg-slate-50 ${!selectedPartner ? 'hidden md:flex' : 'flex'}`}>
        {selectedPartner ? (
          <>
            {/* Chat Header */}
            <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
              <button 
                onClick={() => setSelectedPartner(null)}
                className="md:hidden p-2 hover:bg-slate-100 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                <span className="text-slate-600 font-medium">
                  {selectedPartner.username.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <span className="font-semibold text-slate-800">{selectedPartner.username}</span>
                {isTyping && (
                  <p className="text-xs text-blue-500 animate-pulse">печатает...</p>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-slate-500">Начните диалог</p>
                </div>
              ) : (
                messages.map(msg => {
                  const isOwn = msg.from_user_id === currentUserId;
                  return (
                    <div
                      key={msg.id}
                      className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                      data-testid={`message-${msg.id}`}
                    >
                      <div
                        className={`max-w-[70%] px-4 py-2 rounded-2xl ${
                          isOwn
                            ? 'bg-blue-500 text-white rounded-br-md'
                            : 'bg-white text-slate-800 rounded-bl-md shadow-sm'
                        }`}
                      >
                        <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                        <div className={`text-xs mt-1 flex items-center gap-1 ${isOwn ? 'text-blue-100' : 'text-slate-400'}`}>
                          <span>{new Date(msg.created_at).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}</span>
                          {isOwn && msg.is_read && <span>✓✓</span>}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="bg-white border-t border-slate-200 p-4">
              <form onSubmit={handleSendMessage} className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => {
                    setNewMessage(e.target.value);
                    handleTyping();
                  }}
                  placeholder="Введите сообщение..."
                  className="flex-1"
                  data-testid="message-input"
                />
                <Button type="submit" disabled={!newMessage.trim()} data-testid="send-message-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Выберите диалог или начните новый</p>
            </div>
          </div>
        )}
      </div>

      {/* New Chat Modal */}
      {showUserList && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowUserList(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-800 mb-3">Новый чат</h3>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Поиск пользователей..."
                  className="pl-10"
                  data-testid="search-users-chat"
                />
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {filteredUsers.length === 0 ? (
                <p className="text-center py-8 text-slate-500">Пользователи не найдены</p>
              ) : (
                filteredUsers.map(user => (
                  <button
                    key={user.id}
                    onClick={() => startNewChat(user)}
                    className="w-full flex items-center gap-3 p-4 hover:bg-slate-50 border-b border-slate-100"
                    data-testid={`start-chat-${user.id}`}
                  >
                    <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-slate-500" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-slate-800">{user.username}</p>
                      <p className="text-xs text-slate-500">{user.role}</p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
